import { AsyncPipe, DatePipe } from '@angular/common'
import {
  ChangeDetectionStrategy,
  Component,
  Injector,
  Input,
  OnDestroy,
  Optional,
  Signal,
  SkipSelf,
  inject,
} from '@angular/core'
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop'
import { FormsModule } from '@angular/forms'
import { MatFormField, MatLabel } from '@angular/material/form-field'
import { MatIcon } from '@angular/material/icon'
import { MatInput } from '@angular/material/input'
import { MatSort, MatSortHeader } from '@angular/material/sort'
import {
  MatCell,
  MatCellDef,
  MatColumnDef,
  MatHeaderCell,
  MatHeaderCellDef,
} from '@angular/material/table'
import { Router } from '@angular/router'
import {
  DataContextBuilder,
  ElderMultiSelectChipsComponent,
  ElderSearchBoxComponent,
  ElderSearchContextDirective,
  ElderSearchInputDirective,
  ElderSearchPanelComponent,
  ElderSelectComponent,
  ElderTableComponent,
  ElderTableToolbarDirective,
  ElderTripleStateCheckboxDirective,
  Filter,
  IDataContext,
  IsoIntervalParsePipe,
  Sort,
  ElderDataToolbarComponent,
  ElderToolbarContentDirective,
  SelectionModel,
  ElderToastService,
} from '@elderbyte/ngx-starter'
import { LoggerFactory } from '@elderbyte/ts-logger'
import { ElderTranslatePipe } from '@elderbyte/ngx-starter'
import { BehaviorSubject, Observable, ReplaySubject, switchMap } from 'rxjs'
import { MexusContext } from '../../../context/mexus-context.service'
import { MexusViewMode } from '../../../context/view-mode-context/mexus-view-mode'
import { Facility } from '../../../facilities/facility'
import { OrderCompletionStateSelectDirective } from '../../order-completion-state-select.directive'
import { OrderConfirmationStateSelectDirective } from '../../order-confirmation-state-select.directive'
import { OrderConfirmationState } from '../../prod-orders/prod-order'
import { OrderCompletionState, WorkClearance, WorkOrder } from '../work-order'
import { WorkOrderFilterBuilder } from '../work-order-filter-builder'
import { WorkOrderHintsComponent } from '../work-order-hints/work-order-hints.component'
import { WorkOrderService } from '../work-order.service'
import { MatCheckbox } from '@angular/material/checkbox'
import { MatMenuItem } from '@angular/material/menu'
import {
  MultiWorkorderCommand,
  MultiWorkOrderCommandType,
} from '../commands/multi-work-order-command'

@Component({
  selector: 'app-work-order-browser',
  templateUrl: './work-order-browser.component.html',
  styleUrls: ['./work-order-browser.component.scss'],
  providers: [
    {
      provide: SelectionModel,
      useFactory: (
        _: Injector,
        existingModel: SelectionModel<WorkOrder>
      ): SelectionModel<WorkOrder> => {
        if (existingModel) {
          return existingModel
        } else {
          return new SelectionModel<WorkOrder>(true, [], (workOrder) => workOrder.id)
        }
      },
      deps: [Injector, [new Optional(), new SkipSelf(), SelectionModel]],
    },
  ],
  imports: [
    MatSort,
    MatCheckbox,
    MatFormField,
    MatLabel,
    MatInput,
    MatMenuItem,
    MatColumnDef,
    MatHeaderCellDef,
    MatHeaderCell,
    MatCellDef,
    MatCell,
    MatIcon,
    MatSortHeader,
    ElderTableComponent,
    ElderDataToolbarComponent,
    ElderToolbarContentDirective,
    ElderTableToolbarDirective,
    ElderSearchBoxComponent,
    ElderSearchContextDirective,
    ElderSearchPanelComponent,
    ElderTripleStateCheckboxDirective,
    ElderSelectComponent,
    ElderSearchInputDirective,
    ElderMultiSelectChipsComponent,
    OrderConfirmationStateSelectDirective,
    FormsModule,
    OrderCompletionStateSelectDirective,
    WorkOrderHintsComponent,
    AsyncPipe,
    DatePipe,
    ElderTranslatePipe,
    IsoIntervalParsePipe,
  ],
})
export class WorkOrderBrowserComponent implements OnDestroy {
  /***************************************************************************
   *                                                                         *
   * Fields                                                                  *
   *                                                                         *
   **************************************************************************/

  private readonly log = LoggerFactory.getLogger(this.constructor.name)

  public readonly data: IDataContext<WorkOrder>
  public readonly forcedFiltersByKey$ = new BehaviorSubject<Map<string, Filter>>(
    new Map<string, Filter>()
  )
  private readonly facility$ = new ReplaySubject<string>(1)
  public readonly labels$: Observable<string[]>
  private router = inject(Router)
  private workOrderService = inject(WorkOrderService)
  public readonly selection: Signal<WorkOrder[] | undefined>
  private readonly toastService = inject(ElderToastService)

  /***************************************************************************
   *                                                                         *
   * Constructor                                                             *
   *                                                                         *
   **************************************************************************/

  constructor(public readonly selectionModel: SelectionModel<WorkOrder>) {
    this.data = DataContextBuilder.start<WorkOrder>()
      .pageSize(50)
      .reloadOnLocalChanges()
      .autoStartOnFiltersAll(
        [WorkOrderFilterBuilder.MANUFACTURE_FACILITY_KEY],
        [new Sort('createdAt', 'desc')]
      )
      .build(this.workOrderService)
    this.labels$ = this.facility$.pipe(
      switchMap((facilityId) => this.workOrderService.findLabels(facilityId))
    )
    this.facility$.pipe(takeUntilDestroyed()).subscribe((facilityId) => {
      const filters = WorkOrderFilterBuilder.start()
        .manufactureFacility(facilityId)
        .allowEmpty(false)
        .build()
      const mergedFilters = WorkOrderBrowserComponent.mergeFilters(
        filters,
        this.forcedFiltersByKey$.value
      )
      if (!areMapsEqual(this.forcedFiltersByKey$.value, mergedFilters)) {
        this.forcedFiltersByKey$.next(mergedFilters)
      }
    })
    this.selection = toSignal(selectionModel.selection)
  }

  /***************************************************************************
   *                                                                         *
   * Life Cycle                                                              *
   *                                                                         *
   **************************************************************************/

  public ngOnDestroy(): void {
    if (this.data) {
      this.data.close()
    }
  }

  /***************************************************************************
   *                                                                         *
   * Properties                                                              *
   *                                                                         *
   **************************************************************************/

  @Input()
  public set facility(facility: Facility) {
    if (facility) {
      this.facility$.next(facility.id)
    }
  }

  @Input()
  public set mexusContext(context: MexusContext) {
    if (context) {
      const currentFiltersByKey = this.forcedFiltersByKey$.value
      const updatedFilters = WorkOrderBrowserComponent.buildUpdatedFilters(
        context,
        currentFiltersByKey
      )
      this.log.debug(
        `Setting filter for ${context.viewMode}: ${JSON.stringify([...updatedFilters.values()])}`
      )
      if (!areMapsEqual(currentFiltersByKey, updatedFilters)) {
        this.forcedFiltersByKey$.next(updatedFilters)
      }
    }
  }

  /***************************************************************************
   *                                                                         *
   * Public API                                                              *
   *                                                                         *
   **************************************************************************/

  public isProcessable(order: WorkOrder) {
    return (
      order.orderConfirmationState !== OrderConfirmationState.OPEN &&
      order.clearance === WorkClearance.RELEASABLE
    )
  }

  public openSideDetail(order: WorkOrder): void {
    this.router.navigate([{ outlets: { side: ['work-orders', order.id] } }])
  }

  public openDetail(order: WorkOrder): void {
    if (order) {
      this.router.navigate(['app', 'work-orders', order.id])
    }
  }

  public getFilters(filtersById: Map<string, Filter>): Filter[] {
    return filtersById == null ? null : [...filtersById.values()]
  }

  protected assignMissingProductionRoute(selected: WorkOrder[]) {
    const workOrderIds = new Set(selected.map((wo) => wo.id))
    this.workOrderService
      .executeMultiCommand(<MultiWorkorderCommand>{
        type: MultiWorkOrderCommandType.ASSIGN_PROD_ROUTES,
        ids: [...workOrderIds],
      })
      .subscribe(this.workOrderService.commandSubscriber(this.toastService))
  }

  /***************************************************************************
   *                                                                         *
   * Private methods                                                         *
   *                                                                         *
   **************************************************************************/

  private static mergeFilters(
    newFilters: Filter[],
    currentFiltersByKey?: Map<string, Filter>
  ): Map<string, Filter> {
    const newFiltersByKey = new Map(newFilters.map((filter) => [filter.key, filter]))
    const currentFiltersByKeyOrDefault = currentFiltersByKey ?? new Map<string, Filter>()
    return new Map([...currentFiltersByKeyOrDefault.entries(), ...newFiltersByKey.entries()])
  }

  private static buildUpdatedFilters(
    context: MexusContext,
    currentFiltersByKey: Map<string, Filter>
  ) {
    const current = new Map(currentFiltersByKey)
    switch (context.viewMode) {
      case MexusViewMode.PREVIEW: {
        const merged = WorkOrderBrowserComponent.mergeFilters(
          WorkOrderFilterBuilder.start()
            .manufactureFacility(context.facility.id)
            .scheduledInterval(context.interval)
            .allowEmpty(false)
            .build(),
          current
        )
        merged.delete(WorkOrderFilterBuilder.SCHEDULED_INTERVAL_BEFORE_KEY)
        merged.delete(WorkOrderFilterBuilder.COMPLETION_STATES_KEY)
        return merged
      }
      case MexusViewMode.LIVE: {
        const merged = WorkOrderBrowserComponent.mergeFilters(
          WorkOrderFilterBuilder.start()
            .manufactureFacility(context.facility.id)
            .completionStates(OrderCompletionState.OPEN, OrderCompletionState.PARTIAL)
            .scheduledIntervalBefore(context.liveTimestamp)
            .allowEmpty(false)
            .confirmationStates(OrderConfirmationState.CONFIRMED)
            .build(),
          current
        )
        merged.delete(WorkOrderFilterBuilder.SCHEDULED_INTERVAL_KEY)
        return merged
      }
    }
  }
}

const areMapsEqual: <K, V>(m1: Map<K, V>, m2: Map<K, V>) => boolean = <K, V>(
  m1: Map<K, V>,
  m2: Map<K, V>
) => {
  if (m1.size !== m2.size) {
    return false
  }
  return Array.from(m1.keys()).every((key) => m1.get(key) === m2.get(key))
}
